/**
 * Adaptations feed — reverse-chronological list of every seed/adapt
 * event logged by Phase 3g. The primary "is it working?" view:
 * if this feed is empty after real sessions, adaptation is broken.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  RefreshCw,
  RotateCcw,
  Scissors,
  Sprout,
  TrendingUp,
} from "lucide-react";

import { devListAdaptations } from "@/hooks/useDevDiagnostics";
import type { DevAdaptationEvent } from "@/types/devMode";

import {
  formatCosine,
  formatDuration,
  formatRelativeTime,
  truncateId,
} from "./devFormatters";

/**
 * Refinement outcomes emitted by Phase 3r-5 via adaptation_events.
 * Each row's `kind` is either one of the classic adaptation kinds
 * (seed/adapt/reset) or a refine_* kind below. Display presentation
 * branches on this shape so we can read "did refinement fire?" at a
 * glance in the feed.
 */
const REFINE_OUTCOMES = [
  "refine_split",
  "refine_kept_homogeneous",
  "refine_kept_single_group",
  "refine_kept_too_few_embeds",
  "refine_kept_short_or_empty",
] as const;

type RefineKind = (typeof REFINE_OUTCOMES)[number];

function isRefineKind(kind: string): kind is RefineKind {
  return (REFINE_OUTCOMES as readonly string[]).includes(kind);
}

function refineLabel(kind: RefineKind): string {
  switch (kind) {
    case "refine_split": return "refined · split";
    case "refine_kept_homogeneous": return "refined · kept (homogeneous)";
    case "refine_kept_single_group": return "refined · kept (single group)";
    case "refine_kept_too_few_embeds": return "refined · kept (too few embeds)";
    case "refine_kept_short_or_empty": return "refined · kept (short cluster)";
  }
}

export function AdaptationsTab() {
  const [events, setEvents] = useState<DevAdaptationEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const rows = await devListAdaptations(200);
      setEvents(rows);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error) {
    return <p className="p-4 text-sm text-red-400">Failed to load: {error}</p>;
  }

  if (!events) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-white/40">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading adaptations…
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-white/40">
        No adaptations yet. Record a session with a known contact and come
        back — confident auto-matches on clusters ≥3s with cosine ≥0.75 show
        up here.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center justify-between px-1">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">
          {events.length} event{events.length === 1 ? "" : "s"}
        </p>
        <button
          onClick={refresh}
          className="flex items-center gap-1 text-[11px] text-white/50 active:opacity-60"
        >
          <RefreshCw className="h-3 w-3" />
          refresh
        </button>
      </div>

      {events.map((event) => {
        const isRefine = isRefineKind(event.kind);
        const isReset = event.kind === "reset";
        const isSeed = event.kind === "seed";

        const iconColor = isRefine
          ? "text-violet-300"
          : isReset
            ? "text-amber-300"
            : isSeed
              ? "text-blue-300"
              : "text-emerald-300";

        const icon = isRefine ? (
          <Scissors className="h-4 w-4" />
        ) : isReset ? (
          <RotateCcw className="h-4 w-4" />
        ) : isSeed ? (
          <Sprout className="h-4 w-4" />
        ) : (
          <TrendingUp className="h-4 w-4" />
        );

        // For refine events, the "voiceprintId" is a synthetic
        // "pyannote-cluster-N" identifier — render it as a cluster id
        // rather than a contact id to avoid confusion.
        const headline = isRefine
          ? event.voiceprintId
          : event.voiceprintId === "user_self"
            ? "user_self"
            : truncateId(event.voiceprintId);

        const subline = isRefine
          ? `${refineLabel(event.kind as RefineKind)} · meanCos ${formatCosine(
              event.cosine
            )} · ${formatDuration(event.durationSeconds)}`
          : isReset
            ? `reset to enrollment · ${formatDuration(event.durationSeconds)}`
            : isSeed
              ? `seeded · cosine ${formatCosine(event.cosine)} · ${formatDuration(event.durationSeconds)}`
              : `adapted · cosine ${formatCosine(event.cosine)} · ${formatDuration(event.durationSeconds)}`;

        const details = isRefine
          ? `embeds ${event.samplesBefore} · sub-clusters ${event.samplesAfter} · session ${truncateId(event.sessionId)}`
          : `samples ${event.samplesBefore} → ${event.samplesAfter} · session ${truncateId(event.sessionId)}`;

        return (
          <div
            key={event.id}
            className="flex gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3"
          >
            <div className={`mt-0.5 ${iconColor}`}>{icon}</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-[14px] font-medium text-white">
                  {headline}
                </p>
                <span className="text-[11px] text-white/40">
                  {formatRelativeTime(event.createdAt)}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-white/60">{subline}</p>
              <p className="mt-0.5 text-[11px] text-white/40">{details}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
